const CREATE = "create";
const UPDATE = "update";
const UPSERT = "upsert";
const CREATE_IF_NEW = "createIfNew";

// [lower-case url fragment, allowed methods, kind, extra keys to overwrite (client id -> logged user id)]
const RULES = [
  // ---- users / staff / roles
  ["/api/user/adduser", ["POST"], CREATE],
  ["/api/user/createuser", ["POST"], CREATE],
  ["/api/userrole/addroleasync", ["POST"], CREATE],
  ["/api/userrole/updateroleasync", ["PUT"], UPDATE],
  ["/api/speakeruser/upsert-speakeruser", ["POST"], UPSERT],
  ["/api/address/add-address-by-userid", ["POST"], CREATE],
  ["/api/member/update-member-by-id", ["PUT"], UPDATE],
  ["/api/account/updateusername", ["PUT"], UPDATE],
  ["/api/contact/update-contact-by-userid", ["PUT"], UPDATE],
  ["/api/useractivation/getuseractivationlink", ["POST"], CREATE],
  ["/api/userlegalstaff/upsert-userlegalstaff", ["POST"], UPSERT],
  ["/api/tenant/upserttenant", ["POST"], UPSERT],
  ["/api/moduleconnection", ["POST"], UPSERT],
  // ---- others / feedback / comms
  ["/api/others/add-others", ["POST"], CREATE],
  ["/api/others/update-others", ["PUT"], UPDATE],
  ["/api/memberothersmap/add-member-othersmap", ["POST"], CREATE],
  ["/api/feedback/add-feedback", ["POST"], CREATE],
  ["/api/emailcomm/sendemailtomultiplenattachmentasync", ["POST"], CREATE],
  ["/api/emailcomm/sendemailwithccasync", ["POST"], CREATE],
  ["/api/emailcomm/sendemailwithccnbccasync", ["POST"], CREATE],
  ["/api/textcomm/sendtext", ["POST"], CREATE],
  // ---- files
  ["/fileupload/uploaduserdocuments", ["POST"], CREATE, ["uploadedBy"]],
  ["/api/2/filecabinet/upsertfilecabinetfolder", ["POST"], UPSERT, ["folderCreatedBy"]],
  ["/api/2/userfilecabinet/add", ["POST"], CREATE, ["fileUploadedBy"]],
  // ---- orders / refunds
  ["/api/userorder/adduserorder", ["POST"], CREATE],
  ["/api/userorder/confirmuserorder", ["POST"], UPDATE],
  ["/api/userorder/updateorder", ["PUT"], UPDATE],
  ["/api/userorder/createrefundrequest", ["POST"], CREATE],
  ["/api/userorder/updaterefundrequest", ["PUT"], UPDATE],
  ["/api/userorder/acceptrefundgeneral", ["POST"], UPDATE],
  ["/api/userorder/acceptrefund", ["POST"], UPDATE],
  ["/api/userorder/transferamount", ["POST"], CREATE],
  ["api/userorder/checktransactionstatus", ["POST"], CREATE], // url.js path has no leading slash
  ["/api/userorder/createwithdrawfromaccount", ["POST"], CREATE],
  // ---- subscriptions
  ["/api/userauthsubs/upsert-userauthsubs", ["POST"], UPSERT],
  ["/api/userauthsubs/adduserauthsubsins", ["POST"], CREATE],
  ["/api/userauthsubs/canceluserauthsub", ["POST"], UPDATE],
  ["/api/userauthsubs/createmodifysubsrequest", ["POST"], CREATE],
  ["/api/userauthsubs/updatemodifysubsrequest", ["PUT"], UPDATE],
  ["/api/usersubscriptions/add", ["POST"], CREATE],
  ["/api/usersubscriptions/update", ["PUT"], UPDATE],
  ["/api/usersubscriptionplans/upsert", ["POST"], UPSERT],
  ["/api/usersubscriptions/createuserrecurringsubscription", ["POST"], CREATE],
  ["/api/usersubscriptions/cancelsubscription", ["POST"], UPDATE],
  // card sync is add + edit + delete in one API: updatedBy always, createdBy only for a NEW card (id 0 / missing)
  ["/api/userprofile/syncusercardinfo", ["POST"], UPDATE],
  ["/api/userprofile/syncusercardinfo", ["POST"], CREATE_IF_NEW],
];
// NOTE: OTP APIs (/api/OTP/*) are intentionally excluded: user is not logged in at that point.

// Soft delete: this project deletes by sending isDelete / isDeleted / cardIsDeleted = true
// through the normal write APIs (upsert-UserAuthSubs, SyncUserCardInfo ...).
// Any such object also gets DELETE_FIELD = logged-in user. Change the name here if backend expects another one.
const DELETE_FIELD = "deletedBy";
const DELETE_FLAGS = ["isdelete", "isdeleted", "cardisdeleted"];

const isTrue = (v) => v === true || String(v).toLowerCase() === "true";

const stampDeleted = (node, id) => {
  if (Array.isArray(node)) {
    node.forEach((n) => stampDeleted(n, id));
  } else if (isPlainObject(node)) {
    const keys = Object.keys(node);
    if (keys.some((k) => DELETE_FLAGS.includes(k.toLowerCase()) && isTrue(node[k]))) {
      const existing = keys.find((k) => k.toLowerCase() === DELETE_FIELD.toLowerCase());
      node[existing || DELETE_FIELD] = id;
    }
    keys.forEach((k) => {
      if (node[k] && typeof node[k] === "object") stampDeleted(node[k], id);
    });
  }
};

const FIELD_BY_KIND = { [CREATE]: "createdBy", [CREATE_IF_NEW]: "createdBy", [UPDATE]: "updatedBy", [UPSERT]: "upsertedBy" };
const cleanId = (id) => (id && id !== "null" && id !== "undefined" ? id : null);
export const resolveLoggedUserIdFromPayload = (payload) => {
  if (!isPlainObject(payload)) return null;
  const keys = Object.keys(payload);
  const pick = (wanted) => {
    const k = keys.find((x) => x.toLowerCase() === wanted.toLowerCase());
    return k ? cleanId(payload[k]) : null;
  };
  let state = pick("stateObj");
  if (typeof state === "string") { try { state = JSON.parse(state); } catch (e) { state = null; } }
  const stateUserId = isPlainObject(state) ? cleanId(state.userId) : null;
  return pick("loggedUserId") || stateUserId || pick("createdBy") || pick("upsertedBy") || pick("updatedBy") || null;
};

export const setLoggedUserId = (id) => {
  try {
    if (cleanId(id)) sessionStorage.setItem("loggedUserId", id);
  } catch (e) { }
};

export const getLoggedUserId = () => {
  try {
    const direct = cleanId(sessionStorage.getItem("loggedUserId"));
    if (direct) return direct;
    const stored = JSON.parse(sessionStorage.getItem("queryData") || "null");
    return resolveLoggedUserIdFromPayload(stored);
  } catch (e) {
    return null;
  }
};

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof FormData);

// Overwrite a key that already exists (any letter case) anywhere inside the payload.
const overwriteExisting = (node, field, id) => {
  if (Array.isArray(node)) {
    node.forEach((n) => overwriteExisting(n, field, id));
  } else if (isPlainObject(node)) {
    Object.keys(node).forEach((k) => {
      if (k.toLowerCase() === field.toLowerCase()) node[k] = id;
      else if (node[k] && typeof node[k] === "object") overwriteExisting(node[k], field, id);
    });
  }
};

const stamp = (node, field, id) => {
  if (Array.isArray(node)) {
    node.forEach((n) => stamp(n, field, id));
  } else if (isPlainObject(node)) {
    const existing = Object.keys(node).find((k) => k.toLowerCase() === field.toLowerCase());
    node[existing || field] = id;
  }
};

const stampNew = (node, field, id) => {
  if (Array.isArray(node)) node.forEach((n) => stampNew(n, field, id));
  else if (isPlainObject(node) && !(Number(node.id) > 0)) stamp(node, field, id);
};

const stampForm = (form, field, id) => {
  let key = field;
  for (const k of form.keys()) {
    if (k.toLowerCase() === field.toLowerCase()) { key = k; break; }
  }
  form.set(key, id);
};

const findRules = (method, url) => {
  const m = String(method || "").toUpperCase();
  const u = String(url || "").toLowerCase().split("?")[0];
  return RULES.filter(([frag, methods]) => methods.includes(m) && u.includes(frag));
};

// Returns the same payload object (mutated copy) with audit fields filled.
export const applyAuditFields = (method, url, data) => {
  const loggedUserId = getLoggedUserId();
  if (!loggedUserId || data === undefined || data === null) return data;

  const rules = findRules(method, url);
  if (rules?.length === 0) return data;

  let payload = data;
  const isForm = typeof FormData !== "undefined" && data instanceof FormData;
  if (!isForm) {
    try { payload = JSON.parse(JSON.stringify(data)); } catch (e) { payload = data; }
  }

  rules.forEach(([, , kind, extraKeys]) => {
    const field = FIELD_BY_KIND[kind];
    const keysToSet = [field, ...(extraKeys || [])];
    keysToSet.forEach((f) => {
      if (isForm) stampForm(payload, f, loggedUserId);
      else if (kind === CREATE_IF_NEW) stampNew(payload, f, loggedUserId);
      else if (f === field) stamp(payload, f, loggedUserId);
      else overwriteExisting(payload, f, loggedUserId);
    });
    if (!isForm) {
      // nested lists that already carry the same audit key (cheque list, mobiles, address ...)
      if (kind !== CREATE_IF_NEW) overwriteExisting(payload, field, loggedUserId);
      // upsert payloads that already carry updatedBy (e.g. tenant edit) -> keep it = logged user too
      if (kind === UPSERT) overwriteExisting(payload, "updatedBy", loggedUserId);
    }
  });

  if (!isForm) stampDeleted(payload, loggedUserId);

  return payload;
};